import React from 'react';

import { Control, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { BaseSelect } from 'components/shared/BaseFormComponents/Select';
import Divider from 'components/shared/Divider';
import { FormAccordion, FormAccordionItem } from 'components/shared/FormAccordion';
import { HookCheckbox } from 'components/shared/HookFormComponents';
import { HookInput } from 'components/shared/HookFormComponents/Input';
import { HookWysiwyg } from 'components/shared/HookFormComponents/Wysiwyg';
import Visibility from 'components/shared/Icon/icons/Visibility';
import Text from 'components/shared/Typography/Text';
import { canCreateNewContentPageSelector } from 'store/user/selectors';

import cs from '../../../../index.module.css';
import { ChangePagePopover } from '../../../../shared';
import IconPicker from '../../../../shared/IconPicker';
import { DayOrDatePickers } from '../../DayOrDatePickers';
import s from './index.module.css';
import * as types from './types';
import { useInfoFormContext } from './useInfoForm';

function InfoForm({
  tripEvent,
  loadOptions,
  openPagePreview,
  isVisibleFormBody,
  onCreateOption,
  onChangePage,
  popover,
}: types.FormProps) {
  const { control, watch } = useInfoFormContext();

  // TODO: rework control props for adapters & delete it
  const depricatedControl = control as Control<any>;
  const canCreateNewContent = useSelector(canCreateNewContentPageSelector);
  const isCreateDisabled = !canCreateNewContent;

  return (
    <>
      <FormAccordion>
        <FormAccordionItem header="Required Information" isRequired>
          <div className={cs.row}>
            <div>
              <Text isStrong size="regular">
                Content Page
              </Text>
              <Text color="gray-600" size="medium">
                Introductory Note, About Us, Miscellaneous,
                <br />
                Pre Travel Information, Area, and Activity
                <br />
                pages
              </Text>
            </div>

            <div>
              <Controller
                name="attachedPage"
                control={control}
                render={({ field: { value }, fieldState: { error } }) => {
                  return (
                    <div ref={popover.ref}>
                      <BaseSelect
                        placeholder="Search Content Library"
                        isClearable
                        defaultOption={value}
                        loadOptions={loadOptions}
                        optionActionIcon={Visibility}
                        onOptionActionClick={openPagePreview}
                        onChange={onChangePage}
                        helpText="e.g., Search Property and Miscellaneous pages (Miscellaneous pages won't show on the map)"
                        errorMessage={error?.message}
                        isCreatable
                        isCreateDisabled={isCreateDisabled}
                        onOptionCreation={onCreateOption}
                      />
                    </div>
                  );
                }}
              />

              {popover.isOpen && (
                <ChangePagePopover
                  onClose={popover.onClose}
                  onKeep={popover.onKeep}
                  onReset={popover.onReset}
                  anchorEl={popover.ref.current}
                />
              )}

              <div className={cs.collapseCheckbox}>
                <HookCheckbox name="data.isCollapsible" control={depricatedControl} className={cs.checkboxText}>
                  <Text color="gray-600" size="medium">
                    Collapse content within More Details
                  </Text>
                </HookCheckbox>
              </div>
            </div>
          </div>
          <Divider />

          <div className={cs.row}>
            <div>
              <Text isStrong size="regular">
                Date
              </Text>
            </div>

            <DayOrDatePickers />
          </div>

          <Divider />

          <div className={cs.row}>
            <div>
              <Text isStrong size="regular">
                Icons
              </Text>
              <Text color="gray-600" size="medium">
                Optionally customize your icon,
                <br /> because we’re awesome.
              </Text>
            </div>
            <div>
              <IconPicker name="icon" control={depricatedControl} eventType={tripEvent.type} />
            </div>
          </div>
        </FormAccordionItem>

        {isVisibleFormBody && (
          <>
            <FormAccordionItem header="Event Title">
              <HookInput
                control={depricatedControl}
                name="data.title"
                label={`Title${watch('data.title') ? '' : '*'}`}
              />
            </FormAccordionItem>

            <FormAccordionItem header="Notes">
              <HookWysiwyg
                name="data.details"
                control={depricatedControl}
                placeholder="You have the day at leisure. Here are some recommended things to do."
              />
            </FormAccordionItem>
          </>
        )}
      </FormAccordion>

      <div className={s.marginBottom} />
    </>
  );
}

export { InfoForm };
